import { createBrowserHistory } from 'history';
import noop from './noop';

import { PRESERVED_QUERY_PARAMETERS, TEST } from '../constants';
import { isEmptyString, isNotNil } from './object';

// mock browserHistory when testing
let browserHistory;
if (TEST) {
  // note: we mock the browserHistory API 1:1 when testing so that tests that call functions that
  // use the browserHistoryWrapper will not fail.
  browserHistory = {
    action: 'POP',
    block: noop,
    createHref: noop,
    go: noop,
    goBack: noop,
    goForward: noop,
    length: 0,
    listen: noop,
    location: {},
    push: noop,
    replace: noop,
  };
} else {
  browserHistory = createBrowserHistory();
}

/**
 * Set browserHistory mocks for testing that functions were called.
 * @param {Object} mocks - mocks for browserHistory API.
 */
export function setMocks(mocks) {
  browserHistory = mocks;
}

/**
 * Extract search string from browserHistory location object.
 * @param  {Object} location - browserHistory location object
 * @return {String}          - location search string or empty string if search string is undefined
 */
export function getSearchString(location) {
  return location && location.search ? location.search : '';
}

export function filterSearchString(searchString, allowedParameters) {
  let result = '';

  if (
    searchString &&
    searchString.length &&
    allowedParameters &&
    allowedParameters.length
  ) {
    if (searchString.startsWith('?')) {
      searchString = searchString.substr(1);
    }

    if (searchString) {
      const paramPairs = searchString.split('&');
      const filteredParamString = paramPairs
        .filter((pair) => {
          const pairParts = pair.split('=');
          if (pairParts && pairParts.length) {
            const paramName = pairParts[0];
            if (allowedParameters.includes(paramName)) {
              return true;
            }
          }
          return false;
        })
        .join('&');
      result = `?${filteredParamString}`;
    }
  }

  return result;
}

/**
 * Preserve search string on location args if it is a string. Otherwise pass through args.
 * @param  {Object|String} path       - browserHistory/history location object or string.
 * @param  {Object}        historyArg - arg to mock browserHistory in testing.
 * @return {Object|String}            - browserHistory/history location object or string.
 */
export function convertArgsToLocation(path, historyArg = browserHistory) {
  if (typeof path === 'string') {
    const searchString = getSearchString(historyArg.location);
    return `${path}${filterSearchString(
      searchString,
      PRESERVED_QUERY_PARAMETERS,
    )}`;
  }

  // React router 4+ removes the search query on redirects - if we previously had one then lets set it back.
  if (
    isEmptyString(path.search) &&
    !isEmptyString(historyArg.location.search)
  ) {
    return {
      ...path,
      search: getSearchString(historyArg.location),
    };
  }

  return path;
}

/**
 * Wrapper for browserHistory.push
 * @param  {Object|String} path - browserHistory/history location object or string.
 * @param  {Object?} state - browserHistory/history state object.
 */
function push(path, state) {
  if (isNotNil(state)) {
    return browserHistory.push(convertArgsToLocation(path), state);
  }

  return browserHistory.push(convertArgsToLocation(path));
}

/**
 * Wrapper for browserHistory.replace
 * @param  {Object|String} path - browserHistory/history location object or string.
 * @param  {Object?} state - browserHistory/history state object.
 */
function replace(path, state) {
  if (isNotNil(state)) {
    return browserHistory.replace(convertArgsToLocation(path), state);
  }

  return browserHistory.replace(convertArgsToLocation(path));
}

export const browserHistoryWrapper = Object.assign({}, browserHistory, {
  push,
  replace,
});
